/**
 * Lista de CSTs (Código de Situação Tributária) ICMS
 * Conforme Resolução 65/2008 do CONFAZ e Portaria CAT 01/2020
 *
 * Estrutura:
 * - 00-90: Regime Normal (Lucro Real / Lucro Presumido)
 * - 101-502: Regime de Simples Nacional (CSOSN)
 */

export interface CST {
  codigo: string;
  descricao: string;
  regime: 'normal' | 'simples';
  requerBeneficio: boolean;
}

export const CSTs: CST[] = [
  // ============================================
  // REGIME NORMAL (00-90)
  // ============================================
  {
    codigo: '00',
    descricao: 'Tributada integralmente',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '10',
    descricao: 'Tributada e com cobrança do ICMS por substituição tributária',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '20',
    descricao: 'Tributada com redução de base de cálculo',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '30',
    descricao: 'Isenta ou não tributada e com cobrança do ICMS por substituição tributária',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '40',
    descricao: 'Isenta',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '41',
    descricao: 'Não tributada (Isenção)',
    regime: 'normal',
    requerBeneficio: true // Requer código de benefício fiscal válido
  },
  {
    codigo: '50',
    descricao: 'Suspensão',
    regime: 'normal',
    requerBeneficio: false
  },
  {
    codigo: '51',
    descricao: 'Diferimento',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '53',
    descricao: 'Antecipação de cobrança do ICMS via substituição tributária',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '60',
    descricao: 'ICMS cobrado anteriormente por terceiro',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '61',
    descricao: 'Regime de Substituição Tributária',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '62',
    descricao: 'Tributação concentrada em única fase',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '63',
    descricao: 'Isento de ICMS',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '70',
    descricao: 'Com redução de base de cálculo',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '71',
    descricao: 'Com diferimento de base de cálculo',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '72',
    descricao: 'Pis/Cofins com diferimento',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '73',
    descricao: 'Acordo específico',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '74',
    descricao: 'Base reduzida',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '75',
    descricao: 'Base de cálculo diferida',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '80',
    descricao: 'Com diferimento',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '81',
    descricao: 'Com diferimento de base de cálculo',
    regime: 'normal',
    requerBeneficio: true
  },
  {
    codigo: '90',
    descricao: 'Outras operações',
    regime: 'normal',
    requerBeneficio: false
  },

  // ============================================
  // SIMPLES NACIONAL (101-502)
  // ============================================
  {
    codigo: '101',
    descricao: 'Tributada pelo Simples Nacional com permissão de crédito',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '102',
    descricao: 'Tributada pelo Simples Nacional sem permissão de crédito',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '103',
    descricao: 'Isenção do ICMS no Simples Nacional',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '201',
    descricao: 'Tributada pelo Simples Nacional com permissão de crédito (Serviços)',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '202',
    descricao: 'Tributada pelo Simples Nacional sem permissão de crédito (Serviços)',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '203',
    descricao: 'Isenção do ICMS no Simples Nacional (Serviços)',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '300',
    descricao: 'Imune',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '400',
    descricao: 'Não incidência',
    regime: 'simples',
    requerBeneficio: false
  },
  {
    codigo: '500',
    descricao: 'Suspensão',
    regime: 'simples',
    requerBeneficio: false
  }
];

/**
 * CSTs mais comuns para Ateliê Pro
 */
export const CSTsMaisComuns = [
  CSTs.find(c => c.codigo === '00')!,  // Tributada
  CSTs.find(c => c.codigo === '41')!,  // Isenção
  CSTs.find(c => c.codigo === '102')!, // Simples Nacional
  CSTs.find(c => c.codigo === '103')!, // Simples Nacional Isenta
];

/**
 * Validar se um CST é válido
 */
export const isValidarCST = (cst: string): boolean => {
  return CSTs.some(c => c.codigo === cst);
};

/**
 * Obter informações de um CST
 */
export const getDescricaoCST = (cst: string): string => {
  const item = CSTs.find(c => c.codigo === cst);
  return item ? item.descricao : 'CST inválido';
};

/**
 * Verificar se CST requer código de benefício fiscal
 */
export const cstRequerBeneficio = (cst: string): boolean => {
  const item = CSTs.find(c => c.codigo === cst);
  return item ? item.requerBeneficio : false;
};
